import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { CleanupFamilyTransfersButton } from "./CleanupFamilyTransfersButton";

interface CoryellDocument {
  documentNumber: string;
  recordedDate: string;
  docType: string;
  legalDescription: string;
  pages: string;
  grantees: string[];
  lenderName: string;
  trusteeName: string;
  considerationAmount: number;
}

interface Props {
  onRecordsSaved: () => void;
}

const CORPORATE_KEYWORDS = [
  'BANK', 'MORTGAGE', 'LENDING', 'CREDIT UNION', 'FINANCE', 'FUNDING',
  'LLC', 'LTD', 'INC', 'CORP', 'CORPORATION', 'FEDERAL', 'NATIONAL',
  'SERVICES', 'SOLUTIONS', 'CAPITAL', 'INVESTMENTS', 'HOLDINGS',
  'WELLS FARGO', 'CHASE', 'PENNYMAC', 'DHI', 'USAA', 'NAVY FEDERAL',
  'VETERANS UNITED', 'GUILD', 'CHURCHILL', 'FAIRWAY', 'NEWREZ',
  'BENCHMARK', 'SWBC', 'CANOPY', 'FLAGSTAR', 'EXTRACO', 'FIDELITY',
  'FIRST NATIONAL', 'FIRST FINANCIAL', 'FIRST CONTINENTAL',
  'VANDERBI', 'PLANET HOME', 'VILLAGE CAPITAL', 'ANGEL OAK',
  'UNITED WHOLESALE', 'TRUSTED MORTGAGE', 'SENTE', 'LOAN STORE',
  'HOUSING & URBAN', 'HUD', 'CLEAR2MORTGAGE', 'EQUITY EXPRESS',
  'ARK-LA-TEX', 'PROVIDE', 'MEMBERS CHOICE', '21ST MORTGAGE',
  'PALM HARBOR', 'DHLC', 'MORTGAGE RESEARCH CENTER',
  'MORTGAGE SOLUTIONS', 'KG TEXAS RANCH',
];

export function CoryellCountyTextParser({ onRecordsSaved }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const parseCoryellData = (text: string): CoryellDocument[] => {
    const lines = text.trim().split('\n');
    const documentMap = new Map<string, CoryellDocument>();

    for (const line of lines) {
      const parts = line.split('\t');
      if (parts.length < 7) continue;

      const docNumber = parts[0]?.trim() || '';
      const recordedDate = parts[1]?.trim() || '';
      const consideration1 = parseFloat(parts[2]?.trim()?.replace(/[,$]/g, '') || '0');
      const consideration2 = parseFloat(parts[3]?.trim()?.replace(/[,$]/g, '') || '0');
      const considerationAmount = Math.max(consideration1, consideration2);
      const role = parts[4]?.trim() || '';
      const name = parts[5]?.trim() || '';
      const docType = parts[6]?.trim() || '';
      const legalDescription = parts[7]?.trim() || '';
      const pages = parts[8]?.trim() || '';

      if (!docNumber || !role.toLowerCase().includes('grantee')) continue;

      if (!documentMap.has(docNumber)) {
        documentMap.set(docNumber, {
          documentNumber: docNumber,
          recordedDate,
          docType,
          legalDescription,
          pages,
          grantees: [],
          lenderName: '',
          trusteeName: '',
          considerationAmount: 0,
        });
      }

      const doc = documentMap.get(docNumber)!;
      if (considerationAmount > doc.considerationAmount) {
        doc.considerationAmount = considerationAmount;
      }
      if (name && !doc.grantees.includes(name)) {
        doc.grantees.push(name);
      }

      // Classify this grantee
      const upperName = name.toUpperCase();
      if (upperName.includes('MERS') || upperName === 'MERS, NOM') {
        // Skip MERS nominee
      } else if (upperName.includes(', TR') || upperName.includes(' TR')) {
        doc.trusteeName = name;
      } else {
        // This is the actual lender
        doc.lenderName = name;
      }
    }

    return Array.from(documentMap.values());
  };

  const isCorpLender = (name: string): boolean => {
    const upper = name.toUpperCase();
    return CORPORATE_KEYWORDS.some(kw => upper.includes(kw));
  };

  const handleProcess = async () => {
    if (!textData.trim()) {
      toast({ title: "No data", description: "Please paste records", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    try {
      const documents = parseCoryellData(textData);
      if (documents.length === 0) {
        toast({ title: "No records found", description: "Could not parse any records. Make sure you're copying tab-separated data from the website.", variant: "destructive" });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Hard-filter: remove low-value notes (below $75,000) and corporate lenders
      const MIN_NOTE_AMOUNT = 75000;
      const lowValueCount = documents.filter(doc => doc.considerationAmount > 0 && doc.considerationAmount < MIN_NOTE_AMOUNT).length;
      
      const potentialOwnerFinanced = documents.filter(doc => {
        // Reject if consideration is listed and below minimum
        if (doc.considerationAmount > 0 && doc.considerationAmount < MIN_NOTE_AMOUNT) return false;
        if (!doc.lenderName) return false;
        return !isCorpLender(doc.lenderName);
      });

      const corporateCount = documents.length - lowValueCount - potentialOwnerFinanced.length;
      console.log(`Parsed ${documents.length} docs, ${lowValueCount} below $75K, ${corporateCount} corporate, ${potentialOwnerFinanced.length} potential owner-financed`);

      if (potentialOwnerFinanced.length === 0) {
        toast({
          title: "No owner-financed deals found",
          description: `Analyzed ${documents.length} documents — ${lowValueCount} below $75K, rest had corporate/institutional lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Send remaining to AI for final verification
      const deedText = potentialOwnerFinanced.map(d =>
        `Document: ${d.documentNumber}\n` +
        `Date: ${d.recordedDate}\n` +
        `Grantor: (borrower - not shown in this data)\n` +
        `Beneficiary: ${d.lenderName}\n` +
        `Trustee: ${d.trusteeName}\n` +
        `Legal: ${d.legalDescription}\n`
      ).join('\n---\n');

      const { data: filterData, error: filterError } = await supabase.functions.invoke(
        'filter-owner-finance',
        { body: { deedText } }
      );

      if (filterError) throw filterError;

      const ownerFinancedDeals = filterData?.filteredDeals || [];
      const ownerFinancedDocNumbers = new Set(
        ownerFinancedDeals.map((deal: any) => deal.documentNumber)
      );

      const confirmedRecords = potentialOwnerFinanced.filter(d =>
        ownerFinancedDocNumbers.has(d.documentNumber)
      );

      console.log(`AI confirmed ${confirmedRecords.length} owner-financed deals`);

      if (confirmedRecords.length === 0) {
        toast({
          title: "No owner-financed deals confirmed",
          description: `Hard filter passed ${potentialOwnerFinanced.length}, but AI rejected all as non-individual lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Check for existing records
      const existingQuery = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'coryell')
        .in('document_number', confirmedRecords.map(r => r.documentNumber));

      const existingDocs = new Set(existingQuery.data?.map(d => d.document_number) || []);
      const newRecords = confirmedRecords.filter(r => !existingDocs.has(r.documentNumber));

      if (newRecords.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${confirmedRecords.length} owner-financed deals, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Insert
      const recordsToInsert = newRecords.map(record => ({
        county: 'coryell',
        document_number: record.documentNumber,
        filing_date: record.recordedDate.split(' ')[0] || new Date().toISOString().split('T')[0],
        document_type: record.docType,
        grantor_name: '', // Not available in this data format (only grantees shown)
        grantee_name: record.lenderName,
        legal_description: record.legalDescription,
        number_of_pages: parseInt(record.pages) || null,
        is_potential_owner_finance: true,
        confidence_score: 80,
        detection_reasons: ['AI confirmed individual beneficiary', `Trustee: ${record.trusteeName}`],
        status: 'new',
        raw_data: { grantees: record.grantees, trusteeName: record.trusteeName } as any,
      }));

      const { error: insertError } = await supabase
        .from('county_research')
        .upsert(recordsToInsert, { onConflict: 'document_number', ignoreDuplicates: false });

      if (insertError) throw insertError;

      toast({
        title: "Success!",
        description: `Saved ${newRecords.length} owner-financed deals!\n` +
          `Total: ${documents.length} | Below $75K: ${lowValueCount} | Corporate: ${corporateCount} | ` +
          `AI confirmed: ${confirmedRecords.length} | New saved: ${newRecords.length}`,
      });

      setTextData("");
      onRecordsSaved();
    } catch (error) {
      console.error('Error processing Coryell County data:', error);
      toast({ title: "Error", description: "Failed to process records. Please try again.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const previewCount = textData.trim() ? parseCoryellData(textData).length : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Coryell County, TX - Text Parser with AI Filtering</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Paste deed of trust records from the Coryell County Public Records website. 
            Copy rows directly from the search results table (tab-separated).
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Format:</strong> Doc# [TAB] Date [TAB] 0 [TAB] 0 [TAB] Grantee [TAB] Name [TAB] DocType [TAB] Legal Description [TAB] Pages
          </p>
          <p className="text-sm text-muted-foreground">
            Multiple rows per document are grouped automatically. MERS and Trustee entries are identified separately from the actual lender.
          </p>
        </div>

        <Textarea
          value={textData}
          onChange={(e) => setTextData(e.target.value)}
          placeholder={"379128\t12/26/2024\t0\t0\tGrantee\tFIDELITY BANK OF TEXAS\tDEED OF TRUST\t26.24 AC W BURT A-1808 / 8750 E HWY 84 GV\t9\n379128\t12/26/2024\t0\t0\tGrantee\tSMITH MATT, TR\tDEED OF TRUST\t26.24 AC W BURT A-1808 / 8750 E HWY 84 GV\t9"}
          className="min-h-[300px] font-mono text-sm"
        />

        {previewCount > 0 && (
          <p className="text-sm text-muted-foreground">
            Detected <strong>{previewCount}</strong> unique documents to analyze
          </p>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleProcess}
            disabled={isProcessing || !textData.trim()}
            className="flex-1"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing with AI...
              </>
            ) : (
              `Process ${previewCount > 0 ? previewCount + ' ' : ''}Records`
            )}
          </Button>

          <CleanupFamilyTransfersButton onCleanupComplete={onRecordsSaved} />
        </div>

        {isProcessing && (
          <p className="text-sm text-muted-foreground text-center">
            Hard-filtering corporate lenders, then AI verifying remaining records...
          </p>
        )}
      </CardContent>
    </Card>
  );
}
